/**
 * useScrollShadow — Motion-Trail-Effekt fuer Brand-Headlines.
 * R106.3 (2026-06-07, Stephan-Wunsch). v2 (2026-06-07 abend):
 * Polling-basiert (statt scroll-Event) damit Lenis-Smooth-Scroll nicht blockt.
 *
 * Erzeugt einen dynamischen text-shadow Wert der je nach Scroll-Geschwindigkeit
 * und -Richtung mitlaeuft:
 *   - Scrollen nach unten → Schatten zieht sich nach unten (positive Y)
 *   - Scrollen nach oben  → Schatten zieht sich nach oben (negative Y)
 *   - Schatten ebbt sanft ab wenn Scrollen stoppt
 *
 * Verwendung:
 *   const shadow = useScrollShadow({ maxOffset: 24, maxOpacity: 0.85 })
 *   <h1 style={{ textShadow: shadow }}>FGL Wertung</h1>
 */

'use client'

import { useEffect, useState } from 'react'

export interface ScrollShadowOptions {
  /** maximaler Schatten-Versatz in px (default 24) */
  maxOffset?: number
  /** maximale Schatten-Opacity (default 0.85) */
  maxOpacity?: number
  /** Geschwindigkeit ab der maxOffset erreicht ist (px/frame, default 12) */
  saturateAt?: number
  /** Blur-Faktor relativ zum Offset (default 0.45) */
  blurFactor?: number
  /**
   * Schatten-Farbe als RGB-Triplet (z.B. "0,0,0"). Wenn `undefined`,
   * liest der Hook automatisch die CSS-Variable `--shadow-motion-rgb`
   * (theme-aware) ODER faellt auf `--accent` zurueck.
   */
  color?: string
  /**
   * CSS-Variablen-Quelle fuer auto-color. Default `--accent` (Gold-Akzent).
   * Kann ueberschrieben werden mit z.B. `--text` fuer text-farbenen Schatten.
   */
  cssVar?: string
}

// Parst eine CSS-Color (Hex / rgb()) zu "r, g, b" Triplet.
function parseCssColorToRgbTriplet(raw: string): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (trimmed.startsWith('#')) {
    const hex = trimmed.slice(1)
    const full = hex.length === 3
      ? hex.split('').map(c => c + c).join('')
      : hex.slice(0, 6)
    if (full.length === 6 && /^[0-9a-fA-F]{6}$/.test(full)) {
      const r = parseInt(full.slice(0, 2), 16)
      const g = parseInt(full.slice(2, 4), 16)
      const b = parseInt(full.slice(4, 6), 16)
      return `${r}, ${g}, ${b}`
    }
  }
  if (trimmed.startsWith('rgb')) {
    const nums = trimmed.match(/\d+(?:\.\d+)?/g)
    if (nums && nums.length >= 3) return `${nums[0]}, ${nums[1]}, ${nums[2]}`
  }
  return null
}

/**
 * Liest die theme-aware Shadow-Farbe aus CSS-Variablen.
 * Priorisiert `--shadow-motion-rgb` (falls Theme das definiert),
 * sonst `cssVar` (default `--accent`), sonst schwarz-Fallback.
 */
function readThemeShadowRgb(cssVar: string): string {
  if (typeof window === 'undefined') return '0, 0, 0'
  const root = document.documentElement
  const style = getComputedStyle(root)
  // 1) Explizite shadow-motion-rgb-Variable (Theme-spezifischer Override)
  const explicit = style.getPropertyValue('--shadow-motion-rgb').trim()
  if (explicit) return explicit
  // 2) Quelle-CSS-Variable (default --accent) parsen
  const raw = style.getPropertyValue(cssVar).trim()
  const triplet = parseCssColorToRgbTriplet(raw)
  if (triplet) return triplet
  // 3) Fallback
  return '0, 0, 0'
}

export function useScrollShadow(opts: ScrollShadowOptions = {}): string {
  const {
    maxOffset = 24,
    maxOpacity = 0.85,
    saturateAt = 12,   // px pro Frame (~60fps → 720 px/s = sehr schnelles Scrollen)
    blurFactor = 0.45,
    color,
    cssVar = '--accent',
  } = opts

  // R106.3 (Stephan-Spec 2026-06-07): theme-aware Color. Lest CSS-Vars
  // beim Mount + reagiert auf Theme-Wechsel (data-theme attribute changes).
  const [themeRgb, setThemeRgb] = useState<string>(() =>
    typeof window === 'undefined' ? '0, 0, 0' : readThemeShadowRgb(cssVar)
  )
  useEffect(() => {
    if (color !== undefined) return // expliziter Override — kein Theme-Read
    setThemeRgb(readThemeShadowRgb(cssVar))
    const root = document.documentElement
    const observer = new MutationObserver(() => {
      setThemeRgb(readThemeShadowRgb(cssVar))
    })
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme', 'class', 'style'] })
    return () => observer.disconnect()
  }, [color, cssVar])

  const effectiveColor = color ?? themeRgb

  const [shadow, setShadow] = useState<string>('none')

  useEffect(() => {
    if (typeof window === 'undefined') return

    // Polling per rAF — funktioniert auch wenn Lenis native scroll-Events blockt
    let lastY = window.scrollY
    let velocity = 0          // px/frame, signed
    let rafId = 0
    let stationaryFrames = 0  // Counter wie viele Frames keine Bewegung
    let lastShadow = 'none'

    const tick = () => {
      const y = window.scrollY
      const delta = y - lastY
      lastY = y

      // Velocity als gewichtetes Mittel: 60% Vorher + 40% Neu
      velocity = velocity * 0.6 + delta * 0.4

      if (Math.abs(delta) < 0.1) {
        stationaryFrames++
      } else {
        stationaryFrames = 0
      }

      // Daempfung pro Frame Richtung 0 wenn keine Bewegung
      if (stationaryFrames > 0) {
        velocity *= 0.82
      }

      const v = Math.max(-saturateAt, Math.min(saturateAt, velocity))
      const offset = (v / saturateAt) * maxOffset
      const blur = Math.max(2, Math.abs(offset) * blurFactor + 4)
      const op = Math.min(maxOpacity, (Math.abs(v) / saturateAt) * maxOpacity)

      let next: string
      if (Math.abs(offset) < 0.5) {
        next = 'none'
      } else {
        next = `0 ${offset.toFixed(1)}px ${blur.toFixed(1)}px rgba(${effectiveColor}, ${op.toFixed(3)})`
      }

      // Nur setState wenn Wert sich tatsaechlich geaendert hat (Re-Render-Schutz)
      if (next !== lastShadow) {
        lastShadow = next
        setShadow(next)
      }

      // Loop stoppen wenn 60 Frames (~1s) keine Bewegung + Schatten ist 'none'
      if (stationaryFrames > 60 && next === 'none') {
        rafId = 0
        return
      }
      rafId = requestAnimationFrame(tick)
    }

    // Loop sofort starten — beobachtet kontinuierlich window.scrollY
    rafId = requestAnimationFrame(tick)

    // Fallback: Bei scroll-Event Loop neu starten falls er gestoppt war
    const onScroll = () => {
      stationaryFrames = 0
      if (!rafId) {
        rafId = requestAnimationFrame(tick)
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('wheel', onScroll, { passive: true })
    window.addEventListener('touchmove', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('wheel', onScroll)
      window.removeEventListener('touchmove', onScroll)
      cancelAnimationFrame(rafId)
    }
  }, [maxOffset, maxOpacity, saturateAt, blurFactor, effectiveColor])

  return shadow
}
